/**
 * SourcesModal - Modal for viewing original media sources (audio, photos, transcripts, notes).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Mic, Image, FileText, StickyNote, Paperclip } from 'lucide-react';
import { Button, IconContainer } from '@/components/ui';
import type { SavedDocument } from '@/lib/types';
import type { DocumentSource } from '@/services/documentsApi';
import { documentsApi } from '@/services/documentsApi';
import { getCategoryIcon, getCategoryIconVariant } from '../utils';

interface SourcesModalProps {
  document: SavedDocument;
  onClose: () => void;
}

export const SourcesModal: React.FC<SourcesModalProps> = ({ document, onClose }) => {
  const { t } = useTranslation();
  const [sources, setSources] = useState<DocumentSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const Icon = getCategoryIcon(document.category);
  const iconVariant = getCategoryIconVariant(document.category);

  useEffect(() => {
    let cancelled = false;

    async function fetchSources() {
      setLoading(true);
      setError(null);
      try {
        const result = await documentsApi.getDocumentSources(document.id);
        if (!cancelled) {
          setSources(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(t('projects.sourcesError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchSources();
    return () => { cancelled = true; };
  }, [document.id, t]);

  const audioSources = sources.filter(s => s.type === 'audio');
  const photoSources = sources.filter(s => s.type === 'photo');
  const transcriptSources = sources.filter(s => s.type === 'transcript');
  const noteSources = sources.filter(s => s.type === 'note');

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />

      {/* Panel */}
      <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-3xl sm:rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <IconContainer variant={iconVariant} size="sm" className="flex-shrink-0">
              <Icon className="w-5 h-5" />
            </IconContainer>
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-xl font-bold text-slate-900 truncate">
                {t('projects.sourcesTitle')}
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 truncate mt-0.5">
                {document.title}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label={t('projects.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto mb-2" />
                <p className="text-sm text-slate-500">{t('projects.sourcesLoading')}</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-12">
              <Paperclip className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">{t('projects.sourcesEmpty')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Audio Section */}
              {audioSources.length > 0 && (
                <section>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                    <Mic className="w-4 h-4 text-amber-600" />
                    {t('projects.sourcesAudio')}
                  </h3>
                  <div className="space-y-2">
                    {audioSources.map((source, i) => (
                      <div key={source.filename} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        {audioSources.length > 1 && (
                          <p className="text-xs text-slate-500 mb-1.5">
                            Part {i + 1} of {audioSources.length}
                          </p>
                        )}
                        <audio controls className="w-full" preload="metadata">
                          <source src={source.url} type={source.contentType} />
                        </audio>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Photos Section */}
              {photoSources.length > 0 && (
                <section>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                    <Image className="w-4 h-4 text-blue-600" />
                    {t('projects.sourcesPhotos')}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photoSources.map((source) => (
                      <button
                        key={source.filename}
                        onClick={() => setLightboxUrl(source.url)}
                        className="aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                      >
                        <img
                          src={source.url}
                          alt={source.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Transcript Section */}
              {transcriptSources.length > 0 && (
                <section>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    {t('projects.sourcesTranscript')}
                  </h3>
                  {transcriptSources.map((source) => (
                    <TranscriptBlock key={source.filename} source={source} />
                  ))}
                </section>
              )}

              {/* Notes Section */}
              {noteSources.length > 0 && (
                <section>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                    <StickyNote className="w-4 h-4 text-emerald-600" />
                    {t('projects.sourcesNotes')}
                  </h3>
                  {noteSources.map((source) => (
                    <TranscriptBlock key={source.filename} source={source} />
                  ))}
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 sm:rounded-b-2xl" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <Button variant="dark" size="sm" onClick={onClose}>
            {t('projects.close')}
          </Button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/40 rounded-full transition-colors"
            aria-label={t('projects.close')}
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Fetches and displays text content from a presigned URL.
 */
const TranscriptBlock: React.FC<{ source: DocumentSource }> = ({ source }) => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchContent() {
      try {
        const response = await fetch(source.url);
        const text = await response.text();
        if (!cancelled) setContent(text);
      } catch {
        if (!cancelled) setContent('[Failed to load content]');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchContent();
    return () => { cancelled = true; };
  }, [source.url]);

  if (loading) {
    return (
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-center">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" />
      </div>
    );
  }

  return (
    <pre className="whitespace-pre-wrap font-mono text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-lg p-4 border border-slate-200 mb-2">
      {content}
    </pre>
  );
};

export default SourcesModal;
